import { useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Confirm, Title, useGetList, useNotify, usePermissions } from "react-admin";
import { Link } from "react-router-dom";
import { callApi } from "@/dataprovider/misc-apis";
import { hasPermission } from "@/utils/permission-utils";
import { apiErrorMessage } from "../shared/api-error";
import { MADRASA_ENROLLMENTS_CREATE } from "../shared/madrasa-permissions";
import {
  suggestNextClass,
  suggestNextYear,
  summarizePromotion,
  type ClassOption,
  type PromotionPreview,
  type PromotionResult,
  type YearOption,
} from "./enrollment-utils";

type Selection = {
  fromAcademicYearId: string;
  fromClassId: string;
  toAcademicYearId: string;
  toClassId: string;
  enrolledOn: string;
};

const EMPTY: Selection = {
  fromAcademicYearId: "",
  fromClassId: "",
  toAcademicYearId: "",
  toClassId: "",
  enrolledOn: "",
};

/** Promote a whole class into the next academic year, with a preview before anything is written. */
export default function PromotePage() {
  const { permissions } = usePermissions();
  const notify = useNotify();
  const years = useGetList<YearOption & { id: string }>("academicYears", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "startDate", order: "DESC" },
  });
  const classes = useGetList<ClassOption & { id: string }>("madrasaClasses", {
    pagination: { page: 1, perPage: 200 },
    sort: { field: "sortOrder", order: "ASC" },
  });

  const [sel, setSel] = useState<Selection>(EMPTY);
  const [preview, setPreview] = useState<PromotionPreview | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<PromotionResult | null>(null);
  const [busy, setBusy] = useState<"preview" | "apply" | null>(null);
  const [confirming, setConfirming] = useState(false);

  const yearOptions = years.data ?? [];
  const classOptions = classes.data ?? [];

  const change = (patch: Partial<Selection>) => {
    setSel((current) => {
      const next = { ...current, ...patch };
      // Suggest the natural target so the common case (Class 3 -> Class 4, next year) is one click.
      if (patch.fromAcademicYearId !== undefined && !current.toAcademicYearId) {
        next.toAcademicYearId = suggestNextYear(yearOptions, patch.fromAcademicYearId)?.id ?? "";
      }
      if (patch.fromClassId !== undefined && !current.toClassId) {
        next.toClassId = suggestNextClass(classOptions, patch.fromClassId)?.id ?? "";
      }
      return next;
    });
    // Any change makes an earlier preview stale.
    setPreview(null);
    setResult(null);
  };

  const ready = sel.fromAcademicYearId && sel.fromClassId && sel.toAcademicYearId && sel.toClassId;

  const body = useMemo(
    () => ({
      fromAcademicYearId: sel.fromAcademicYearId,
      fromClassId: sel.fromClassId,
      toAcademicYearId: sel.toAcademicYearId,
      toClassId: sel.toClassId,
      ...(sel.enrolledOn ? { enrolledOn: sel.enrolledOn } : {}),
    }),
    [sel]
  );

  const runPreview = async () => {
    setBusy("preview");
    setResult(null);
    try {
      const response = await callApi({
        location: "madrasaEnrollments/promote-preview",
        method: "POST",
        data: body,
      });
      const data = response.data as PromotionPreview;
      setPreview(data);
      setChecked(new Set(data.eligible.map((e) => e.studentId)));
    } catch (error: unknown) {
      setPreview(null);
      notify(apiErrorMessage(error, "Could not preview the promotion"), { type: "error" });
    } finally {
      setBusy(null);
    }
  };

  const runApply = async () => {
    setConfirming(false);
    setBusy("apply");
    try {
      const response = await callApi({
        location: "madrasaEnrollments/promote-apply",
        method: "POST",
        data: { ...body, studentIds: Array.from(checked) },
      });
      const data = response.data as PromotionResult;
      setResult(data);
      setPreview(null);
      notify(summarizePromotion(data), { type: "success" });
    } catch (error: unknown) {
      notify(apiErrorMessage(error, "Could not promote the students"), { type: "error" });
    } finally {
      setBusy(null);
    }
  };

  if (!hasPermission(permissions, MADRASA_ENROLLMENTS_CREATE)) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">You do not have permission to promote students.</Alert>
      </Box>
    );
  }

  const loading = years.isPending || classes.isPending;
  const allChecked =
    preview != null && preview.eligible.length > 0 && checked.size === preview.eligible.length;

  const toggle = (id: string) =>
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <>
      <Title title="Promote students" />
      <Box sx={{ p: 2, maxWidth: 820 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Promote students
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Move a class into the next academic year. You will see exactly who is promoted and who is
          skipped before anything is saved.
        </Typography>

        {loading ? (
          <CircularProgress size={28} />
        ) : (
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="From academic year"
                  value={sel.fromAcademicYearId}
                  onChange={(e) => change({ fromAcademicYearId: e.target.value })}
                  fullWidth
                >
                  {yearOptions.map((y) => (
                    <MenuItem key={y.id} value={y.id}>
                      {y.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="From class"
                  value={sel.fromClassId}
                  onChange={(e) => change({ fromClassId: e.target.value })}
                  fullWidth
                >
                  {classOptions.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="To academic year"
                  value={sel.toAcademicYearId}
                  onChange={(e) => change({ toAcademicYearId: e.target.value })}
                  fullWidth
                >
                  {yearOptions
                    .filter((y) => !y.isClosed)
                    .map((y) => (
                      <MenuItem key={y.id} value={y.id}>
                        {y.name}
                      </MenuItem>
                    ))}
                </TextField>
                <TextField
                  select
                  label="To class"
                  value={sel.toClassId}
                  onChange={(e) => change({ toClassId: e.target.value })}
                  fullWidth
                >
                  {classOptions
                    .filter((c) => c.isActive !== false)
                    .map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        {c.name}
                      </MenuItem>
                    ))}
                </TextField>
              </Stack>
              <TextField
                label="Enrolled on (optional)"
                type="date"
                value={sel.enrolledOn}
                onChange={(e) => change({ enrolledOn: e.target.value })}
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="Fees start from this month. Leave empty to start on the first day of the target academic year."
                sx={{ maxWidth: 360 }}
              />
              <Box>
                <Button variant="contained" onClick={runPreview} disabled={!ready || busy !== null}>
                  {busy === "preview" ? "Checking…" : "Preview"}
                </Button>
              </Box>
            </Stack>
          </Paper>
        )}

        {result && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {summarizePromotion(result)} <Link to="/madrasaEnrollments">View enrollments</Link>
            {result.skipped.length > 0 && (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
                {result.skipped.map((s) => (
                  <li key={s.studentId}>
                    {s.name ?? "Unknown student"}: {s.reasonText}
                  </li>
                ))}
              </Box>
            )}
          </Alert>
        )}

        {preview && (
          <>
            <Alert severity={preview.eligible.length ? "info" : "warning"} sx={{ mb: 2 }}>
              {preview.eligible.length
                ? `${preview.eligible.length} ${preview.eligible.length === 1 ? "student" : "students"} can move from ${preview.target.fromClass} (${preview.target.fromAcademicYear}) to ${preview.target.toClass} (${preview.target.toAcademicYear}), with fees starting ${preview.target.enrolledOn}.`
                : "Nobody in this class can be promoted with these settings."}
            </Alert>

            {preview.eligible.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allChecked}
                          indeterminate={checked.size > 0 && !allChecked}
                          onChange={() =>
                            setChecked(
                              allChecked
                                ? new Set()
                                : new Set(preview.eligible.map((e) => e.studentId))
                            )
                          }
                          inputProps={{ "aria-label": "Select all students" }}
                        />
                      </TableCell>
                      <TableCell>Adm. no</TableCell>
                      <TableCell>Student</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.eligible.map((e) => (
                      <TableRow
                        key={e.studentId}
                        hover
                        onClick={() => toggle(e.studentId)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox checked={checked.has(e.studentId)} />
                        </TableCell>
                        <TableCell>{e.admissionNo}</TableCell>
                        <TableCell>{e.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}

            {preview.skipped.length > 0 && (
              <Paper variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Skipped ({preview.skipped.length})
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {preview.skipped.map((s) => (
                    <li key={s.studentId}>
                      {s.name ?? "Unknown student"}
                      {s.admissionNo != null ? ` (adm. ${s.admissionNo})` : ""}: {s.reasonText}
                    </li>
                  ))}
                </Box>
              </Paper>
            )}

            {preview.eligible.length > 0 && (
              <Button
                variant="contained"
                onClick={() => setConfirming(true)}
                disabled={checked.size === 0 || busy !== null}
              >
                {busy === "apply"
                  ? "Promoting…"
                  : `Promote ${checked.size} ${checked.size === 1 ? "student" : "students"}`}
              </Button>
            )}
          </>
        )}

        <Confirm
          isOpen={confirming}
          title={`Promote ${checked.size} ${checked.size === 1 ? "student" : "students"}?`}
          content={
            preview
              ? `They will be enrolled in ${preview.target.toClass} for ${preview.target.toAcademicYear}, with fees starting ${preview.target.enrolledOn}. Their ${preview.target.fromAcademicYear} enrollments are left as they are.`
              : ""
          }
          onConfirm={runApply}
          onClose={() => setConfirming(false)}
        />
      </Box>
    </>
  );
}
